import type { ReactNode } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useSession } from "../lib/session";
import { signOut } from "../lib/auth";
import { api, t } from "../lib/api";
import { useAsync } from "../lib/hooks";

export function Shell({ children }: { children: ReactNode }) {
  const { me, config } = useSession();
  const { tenant } = useParams();
  const nav = useNavigate();
  const ws = me?.workspaces.find((w) => w.slug === tenant);
  const engagements = useAsync(
    () => (tenant ? api.get<Array<{ id: string; name: string }>>(`${t(tenant)}/engagements`) : Promise.resolve([])),
    [tenant],
  );

  return (
    <div className="shell">
      <aside className="sidebar">
        <NavLink to="/" className="brandmark">
          <img src="/brand/gramin-kriti-mark-light-small.svg" width={34} height={34} alt="" />
          <div>
            <b>Gramin Kriti</b>
            <span>Kriti Voice</span>
          </div>
        </NavLink>
        {me && me.workspaces.length > 0 && (
          <div>
            <div className="nav-label">Client workspace</div>
            <select aria-label="Client workspace" value={tenant ?? ""} onChange={(e) => nav(`/w/${e.target.value}`)}>
              {!tenant && <option value="">Choose…</option>}
              {me.workspaces.map((w) => (
                <option key={w.slug} value={w.slug}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {tenant && (
          <nav className="nav" aria-label="Workspace">
            <NavLink to={`/w/${tenant}`} end>
              Engagements
            </NavLink>
            {(engagements.data ?? []).map((e) => (
              <NavLink key={e.id} to={`/w/${tenant}/e/${e.id}`} style={{ paddingLeft: 22 }}>
                {e.name}
              </NavLink>
            ))}
            {ws && (ws.role === "admin" || me?.user.isPlatformAdmin) && <NavLink to={`/w/${tenant}/team`}>Team and access</NavLink>}
          </nav>
        )}
        {me?.user.isPlatformAdmin && (
          <nav className="nav" aria-label="Platform">
            <div className="nav-label">Platform</div>
            <NavLink to="/admin/ai">AI providers</NavLink>
            <NavLink to="/admin/clients">Clients</NavLink>
            <NavLink to="/admin/users">Users</NavLink>
            <NavLink to="/admin/security">Security and audit</NavLink>
          </nav>
        )}
        <div className="whoami">
          <div style={{ color: "#fff", fontWeight: 600 }}>{me?.user.name ?? me?.user.email}</div>
          <div>{me?.user.email}</div>
          {ws && <div style={{ marginTop: 4 }}>Role: {roleLabel(ws.role)}</div>}
          {config.demoProviders && <div className="pill ochre" style={{ marginTop: 8 }}>Demo mode — simulated speech and AI</div>}
          <button
            className="btn ghost small"
            style={{ color: "#cfdcd5", marginTop: 8, paddingLeft: 0 }}
            onClick={async () => {
              await signOut();
              nav("/login");
            }}
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="main">{children}</main>
    </div>
  );
}

export function roleLabel(role: string): string {
  return (
    { admin: "Workspace admin", lead: "Engagement lead", reviewer: "Reviewer", interviewer: "Interviewer", client_viewer: "Client viewer" } as Record<string, string>
  )[role] ?? role;
}
